<script setup>
import { Head } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import ManagementHeader from "../../../Components/Management/ManagementHeader.vue";
import MonthlySpecialDayCalendar from "../../../Components/Management/MonthlySpecialDayCalendar.vue";
import SpecialDayEditorModal from "../../../Components/Management/SpecialDayEditorModal.vue";
import Dropdown from "primevue/dropdown";
import { ref, watch, computed } from "vue";
import { Inertia } from "@inertiajs/inertia";
import { usePage } from "@inertiajs/inertia-vue3";

const props = defineProps([
    'companies',
    'selectedCompany',
    'filters',
    'flash'
]);

const route   = window.route;
const page    = usePage();
const isSuperAdmin = computed(() => page.props.value?.auth?.user?.role === 'super_admin');

const selectedCompanyUuid = ref(props.filters?.company);
const calendarRef = ref(null);

// Modal State
const editorVisible = ref(false);
const selectedDate = ref('');
const editingRecord = ref(null);
const defaultHoursForSelectedDate = ref(null);

const handleCompanyChange = (e) => {
    selectedCompanyUuid.value = e.value;
    Inertia.get(route('management.companies.special-days'), { company: e.value }, {
        preserveState: false,
        preserveScroll: true,
        only: ['selectedCompany', 'filters']
    });
};

const openEditor = (dateStr) => {
    selectedDate.value = dateStr;
    
    // Find if we already have a special day for this date in the company data
    // Note: The calendar component also has its own map, but we can look into props.selectedCompany.special_days
    editingRecord.value = props.selectedCompany.special_days?.find(d => {
        const start = (d.start_date || d.date || '').split('T')[0];
        const end = (d.end_date || start || '').split('T')[0];
        return dateStr >= start && dateStr <= end;
    }) || null;
    
    // Calculate default hours based on weekday
    const date = new Date(dateStr);
    const dow = date.getDay(); // 0=Sun
    defaultHoursForSelectedDate.value = props.selectedCompany.working_hours?.find(h => h.day_of_week === dow) || null;
    
    editorVisible.value = true;
};

const onSaved = () => {
    // We need to refresh the props to get the updated special_days list
    // Inertia.reload will refresh only the specific keys
    Inertia.reload({
        only: ['selectedCompany'],
        onSuccess: () => {
            if (calendarRef.value) calendarRef.value.refresh();
        }
    });
};

const onCleared = () => {
    Inertia.reload({
        only: ['selectedCompany'],
        onSuccess: () => {
            if (calendarRef.value) calendarRef.value.refresh();
        }
    });
};

</script>

<template>
    <Layout :flash="flash">
        <Head title="Feiertage Hub" />

        <div class="space-y-6">
            <ManagementHeader title="Unternehmen Management" module="companies" :activeIndex="2"
                               :tabOverrides="isSuperAdmin ? [] : ['Unternehmensdetails']" />

            <!-- Top Selector Section — super_admin only -->
            <div v-if="isSuperAdmin"
                 class="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
                        <i class="pi pi-users text-gray-400" />
                    </div>
                    <div>
                        <h3 class="font-bold text-gray-700">Unternehmen wählen</h3>
                        <p class="text-xs text-gray-400">Feiertage und Sondertage für Standorte verwalten</p>
                    </div>
                </div>
                
                <div class="w-full md:w-80">
                    <Dropdown 
                        v-model="selectedCompanyUuid" 
                        @change="handleCompanyChange"
                        :options="companies" 
                        optionLabel="name" 
                        optionValue="uuid" 
                        placeholder="Standort suchen..." 
                        filter 
                        class="w-full rounded-xl border-gray-200"
                    >                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <span class="font-medium">{{ slotProps.option.name }}</span>
                            </div>
                        </template>
                    </Dropdown>
                </div>
            </div>

            <!-- Content Area -->
            <div v-if="selectedCompany" class="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div class="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                    <!-- Company Info Bar -->
                    <div class="px-8 py-6 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
                        <div class="flex items-center gap-4">
                            <div class="w-12 h-12 rounded-2xl bg-black flex items-center justify-center text-[#FFF053] font-bold text-xl">
                            {{ selectedCompany.name.charAt(0) }}
                        </div>
                        <div>
                                <h2 class="text-xl font-bold text-gray-800">{{ selectedCompany.name }}</h2>
                                <p class="text-sm text-gray-400">Feiertage & Standort-Ausnahmen</p>
                            </div>
                        </div>
                    </div>

                    <div class="px-6 pb-6">
                        <MonthlySpecialDayCalendar 
                            ref="calendarRef"
                            type="company"
                            :companyId="selectedCompany.id"
                            title="Standort-Sondertage"
                            @date-selected="openEditor"
                        />
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="isSuperAdmin" class="py-20 text-center bg-gray-50/50 rounded-3xl border border-dashed border-gray-200">
                <div class="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-4">
                    <i class="pi pi-search text-2xl text-gray-300" />
                </div>
                <h3 class="text-lg font-bold text-gray-400">Kein Standort ausgewählt</h3>
                <p class="text-gray-300">Bitte wählen Sie oben einen Standort aus, um Sondertage zu bearbeiten.</p>
            </div>
        </div>

        <!-- Editor Modal -->
        <SpecialDayEditorModal 
            v-if="selectedCompany"
            v-model:visible="editorVisible"
            :date="selectedDate"
            type="company"
            :companyId="selectedCompany.id"
            :existing="editingRecord"
            :defaultHours="defaultHoursForSelectedDate"
            @saved="onSaved"
            @cleared="onCleared"
        />
    </Layout>
</template>
